import styled from 'styled-components';
import COLORS from '../../utils/Colors';
import { NAVBAR_HEIGHT } from '../../utils/constants';

const BRAND_COLOR = '#5714c9';

export const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: ${NAVBAR_HEIGHT};
    min-height: 100vh;
    background-color: ${COLORS.freshaGray50};
`;

export const Container = styled.div`
    display: flex;
    width: 100%;
    max-width: 500px;
    flex-direction: column;
    padding: 2rem 1rem;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        padding: 1rem;
    };
`;

export const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
`;

export const Logo = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 16px;
`;

export const Card = styled.div`
    background: ${COLORS.white};
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid ${COLORS.freshaGray200};

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        padding: 1.5rem;
    }
`;

export const Title = styled.h1`
    font-family: 'Raleway', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
    margin: 0 0 1.5rem 0;
    text-align: center;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        font-size: 1.25rem;
    }
`;

export const BookingCard = styled.div`
    background: ${COLORS.freshaGray50};
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-left: 4px solid ${BRAND_COLOR};
`;

export const BusinessName = styled.h3`
    font-family: 'Raleway', sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
    margin: 0 0 1rem 0;
`;

export const DetailRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${COLORS.freshaGray700};
    font-size: 0.875rem;
    margin-bottom: 0.5rem;

    &:last-child {
        margin-bottom: 0;
    }

    svg {
        color: ${COLORS.freshaGray500};
        flex-shrink: 0;
    }
`;

export const ServicesList = styled.ul`
    margin: 0.5rem 0 0 1.5rem;
    padding: 0;
    color: ${COLORS.freshaGray600};
    font-size: 0.875rem;
`;

export const ServiceItem = styled.li`
    margin-bottom: 0.25rem;
`;

export const TotalCost = styled.div`
    font-size: 1.25rem;
    font-weight: 700;
    color: ${BRAND_COLOR};
    margin-top: 1rem;
    text-align: right;
`;

export const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const Button = styled.button<{ $variant: 'danger' | 'secondary' }>`
    width: 100%;
    padding: 14px 20px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    ${({ $variant }) => {
        switch ($variant) {
            case 'danger':
                return `
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    &:hover:not(:disabled) {
                        background-color: #c82333;
                    }
                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                `;
            case 'secondary':
                return `
                    background-color: white;
                    color: ${COLORS.freshaGray700};
                    border: 1px solid ${COLORS.freshaGray200};
                    &:hover {
                        background-color: ${COLORS.freshaGray100};
                    }
                `;
        }
    }}
`;

export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    color: ${COLORS.freshaGray500};
    gap: 1rem;
`;

export const Spinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid ${COLORS.freshaGray200};
    border-top-color: ${BRAND_COLOR};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export const ErrorContainer = styled.div`
    text-align: center;
    padding: 2rem;
`;

export const ErrorText = styled.p`
    color: #dc3545;
    font-size: 1rem;
    margin: 0 0 1.5rem 0;
`;

export const SuccessContainer = styled.div`
    text-align: center;
`;

export const SuccessIcon = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #d4edda;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: #28a745;
`;

export const SuccessTitle = styled.h2`
    font-family: 'Raleway', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
    margin: 0 0 0.5rem 0;
`;

export const SuccessText = styled.p`
    color: ${COLORS.freshaGray600};
    font-size: 1rem;
    margin: 0 0 2rem 0;
`;

export const Footer = styled.div`
    text-align: center;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid ${COLORS.freshaGray200};
`;

export const FooterLink = styled.a`
    color: ${BRAND_COLOR};
    text-decoration: none;
    font-weight: 600;
    font-size: 0.875rem;

    &:hover {
        text-decoration: underline;
    }
`;

export const FooterText = styled.p`
    color: ${COLORS.freshaGray500};
    font-size: 0.75rem;
    margin: 0.5rem 0 0 0;
`;
